import React from "react";

// reactstrap components
import {
  Card,
  CardBody,
  CardTitle,
  Container,
  Row,
  Col,
  CardFooter,
  Button,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import axios from "axios";
import { connect } from "react-redux";
import { addAssignedScripts, addMarkedScripts } from "actions/index";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  readPersistedStatsYear,
  persistStatsYear,
  getCurrentStatsYearBounds,
  isValidStatsYear,
  readYearStatsCache,
  writeYearStatsCache,
  clearYearStatsCache,
  clearMarkerMarkedScriptsCache,
  DASHBOARD_STATS_REFRESH_INTERVAL_MS,
  readMarkerMarkedScriptsCache,
  writeMarkerMarkedScriptsCache,
  countScriptsForReportingYear
} from "utils/dashboardStatsCache";
import { fetchAdminDashboardFunnel } from "utils/adminDashboardFunnel";
import AdminTierAMetrics from "components/Headers/AdminTierAMetrics";
import AdminMarkerWorkloadSection from "components/Headers/AdminMarkerWorkloadSection";
import AdminMarkerWorkloadCharts from "components/Headers/AdminMarkerWorkloadCharts";

/** Skeleton placeholder for dashboard stat value (shimmer via SCSS). */
function DashboardStatSkeleton({ label }) {
  return (
    <span className="d-inline-flex flex-column" aria-busy="true">
      <span className="sr-only">Loading {label}</span>
      <span className="dashboard-stat-skeleton__value" aria-hidden="true" />
    </span>
  );
}

function mapDispatchToProps(dispatch) {
  return {
    addAssignedScripts: assigned_scripts => dispatch(addAssignedScripts(assigned_scripts)),
    addMarkedScripts: marked_scripts => dispatch(addMarkedScripts(marked_scripts))
  };
}

class Header extends React.Component {

  constructor(props) {
    super(props);
    this._isMounted = false;
    const role = localStorage.getItem("userRole");
    const showStats = role === "app-admin" || role === "marker";
    const persistedYear = readPersistedStatsYear();
    const { max: currentCalendarYear } = getCurrentStatsYearBounds();
    const statsYear =
      persistedYear != null ? persistedYear : currentCalendarYear;
    this.state = {
      assigned_scripts: 0,
      marked_scripts: [],
      admin_available: 0,
      admin_marked: 0,
      role,
      assigned_total: 0,
      statsYear,
      markerAssessedCount: 0,
      marker_codes: [],
      funnel: null,
      funnelLoading: false,
      statsLoading: {
        assessed: showStats,
        assigned: showStats,
        available: role === "app-admin"
      }
    };
    this.getUserMarkerCodes = this.getUserMarkerCodes.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    const role = localStorage.getItem("userRole");
    if (role === "app-admin") {
      this.refreshAdminDashboardStats(this.state.statsYear);
    } else if (role === "marker") {
      this.getUserMarkerCodes();
    }
    this.startDashboardAutoRefresh();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.stopDashboardAutoRefresh();
  }

  /** Invalidate cache and refetch headline + funnel stats on a fixed interval. */
  startDashboardAutoRefresh() {
    this.stopDashboardAutoRefresh();
    const role = localStorage.getItem("userRole");
    if (role !== "app-admin" && role !== "marker") return;
    this._dashboardRefreshTimer = setInterval(() => {
      if (!this._isMounted) return;
      const year = this.state.statsYear;
      const r = this.state.role;
      if (!isValidStatsYear(year)) return;
      if (r === "app-admin") {
        clearYearStatsCache("app-admin", year);
        this.refreshAdminDashboardStats(year);
      } else if (r === "marker" && (this.state.marker_codes || []).length > 0) {
        clearYearStatsCache("marker", year);
        clearMarkerMarkedScriptsCache();
        this.refreshMarkerDashboardStats(year);
      }
    }, DASHBOARD_STATS_REFRESH_INTERVAL_MS);
  }

  stopDashboardAutoRefresh() {
    if (this._dashboardRefreshTimer != null) {
      clearInterval(this._dashboardRefreshTimer);
      this._dashboardRefreshTimer = null;
    }
  }

  handleStatsYearChange = e => {
    const y = parseInt(e.target.value, 10);
    if (!isValidStatsYear(y)) return;
    persistStatsYear(y);
    this.setState({ statsYear: y }, () => {
      if (this.state.role === "app-admin") {
        this.refreshAdminDashboardStats(y);
      } else if (this.state.role === "marker") {
        this.refreshMarkerDashboardStats(y);
      }
    });
  };

  /** Admin: cache 30m per reporting year; auto-refresh clears cache on interval. `year` query/body for backend filtering when supported. */
  refreshAdminDashboardStats = async year => {
    if (!isValidStatsYear(year)) return;
    const cached = readYearStatsCache("app-admin", year);
    if (
      cached &&
      typeof cached.admin_marked === "number" &&
      typeof cached.admin_available === "number" &&
      typeof cached.assigned_total === "number"
    ) {
      if (!this._isMounted) return;
      this.setState({
        admin_marked: cached.admin_marked,
        admin_available: cached.admin_available,
        assigned_total: cached.assigned_total,
        funnel: cached.funnel != null ? cached.funnel : null,
        funnelLoading: cached.funnel == null,
        statsLoading: { assessed: false, assigned: false, available: false }
      });
      if (cached.funnel == null) {
        fetchAdminDashboardFunnel(year, this.adminBearerGetHeaders()).then(
          funnel => {
            if (!this._isMounted) return;
            this.setState({ funnel, funnelLoading: false });
            writeYearStatsCache("app-admin", year, {
              admin_marked: cached.admin_marked,
              admin_available: cached.admin_available,
              assigned_total: cached.assigned_total,
              funnel
            });
          }
        );
      }
      return;
    }

    if (!this._isMounted) return;
    this.setState({
      statsLoading: { assessed: true, assigned: true, available: true },
      funnelLoading: true
    });

    try {
      const base = "https://admin.thinkdigitalacademy.org/api/get-admin-dashboard-stats";
      const url = `${base}?year=${encodeURIComponent(year)}`;
      let res;
      try {
        res = await axios.get(url);
      } catch {
        res = await axios.get(url, { headers: this.adminBearerGetHeaders() });
      }
      const d = res.data || {};
      const admin_marked = Number(d.marked_count) || 0;
      const admin_available = Number(d.available_count) || 0;
      let assigned_total =
        d.assigned_count != null && !Number.isNaN(Number(d.assigned_count))
          ? Number(d.assigned_count)
          : null;

      if (this._isMounted) {
        this.setState({
          admin_marked,
          admin_available,
          statsLoading: { assessed: false, assigned: true, available: false }
        });
      }

      let degraded = false;
      // Marker codes / assigned counts come from marker-portal-backend. If that service is down it must only
      // degrade the "Assigned" card; headline counts and the funnel below do not depend on it.
      try {
        let marker_codes = this.state.marker_codes;
        if (!marker_codes || marker_codes.length === 0) {
          const token = localStorage.getItem("access_token");
          const cr = await axios.get(
            "https://markerportalbackend.thinkdigitalacademy.org/api/get-all-assigned-marker-codes",
            { headers: { Authorization: `Bearer ${token}` } }
          );
          marker_codes = Array.isArray(cr.data && cr.data.marker_codes)
            ? cr.data.marker_codes
            : [];
          if (this._isMounted) this.setState({ marker_codes });
        }

        if (marker_codes && marker_codes.length > 0) {
          const gr = await axios({
            headers: this.adminAuthHeaders(),
            method: "post",
            url: "https://admin.thinkdigitalacademy.org/api/marker-portal-get-grades-assigned",
            data: { marker_codes, year }
          });
          const grades = (gr.data && gr.data.grades) || [];
          assigned_total = grades.reduce(
            (s, g) => s + (Number(g.uploads) || 0),
            0
          );
        }
      } catch {
        // fall through: assigned_total stays as sent by the stats endpoint (or null)
        degraded = assigned_total == null;
      }
      if (assigned_total == null) assigned_total = 0;

      const funnel = await fetchAdminDashboardFunnel(
        year,
        this.adminBearerGetHeaders()
      );
      if (!this._isMounted) return;
      const at = assigned_total || 0;
      this.setState({
        admin_marked,
        admin_available,
        assigned_total: at,
        funnel,
        funnelLoading: false,
        statsLoading: { assessed: false, assigned: false, available: false }
      });
      if (!degraded && funnel != null) {
        writeYearStatsCache("app-admin", year, {
          admin_marked,
          admin_available,
          assigned_total: at,
          funnel
        });
      }
    } catch {
      if (!this._isMounted) return;
      this.setState({
        statsLoading: { assessed: false, assigned: false, available: false },
        funnelLoading: false
      });
    }
  };

  /** Marker: cache scripts 30m; per-year assessed = filter created_at; auto-refresh clears caches on interval. */
  refreshMarkerDashboardStats = async year => {
    if (!isValidStatsYear(year)) return;
    const codes = this.state.marker_codes || [];
    const cachedYear = readYearStatsCache("marker", year);
    let scripts = readMarkerMarkedScriptsCache();

    if (
      cachedYear &&
      typeof cachedYear.assigned_total === "number" &&
      scripts != null &&
      Array.isArray(scripts)
    ) {
      if (!this._isMounted) return;
      const markerAssessedCount = countScriptsForReportingYear(scripts, year);
      this.setState({
        assigned_total: cachedYear.assigned_total,
        markerAssessedCount,
        marked_scripts: scripts,
        statsLoading: { assessed: false, assigned: false }
      });
      this.props.addAssignedScripts(scripts);
      return;
    }

    if (!this._isMounted) return;
    this.setState({ statsLoading: { assessed: true, assigned: true } });

    try {
      if (scripts == null) {
        const res = await axios({
          headers: this.adminAuthHeaders(),
          method: "post",
          url: "https://admin.thinkdigitalacademy.org/api/get-marked-scripts",
          data: { marker_codes: codes, year }
        });
        scripts = res.data.scripts || [];
        writeMarkerMarkedScriptsCache(scripts);
        if (this._isMounted) this.props.addAssignedScripts(scripts);
      }

      const markerAssessedCount = countScriptsForReportingYear(scripts, year);

      let assigned_total = cachedYear && cachedYear.assigned_total;
      if (assigned_total == null) {
        const base = "https://admin.thinkdigitalacademy.org/api/get-admin-dashboard-stats";
        const url = `${base}?year=${encodeURIComponent(year)}`;
        let res;
        try {
          res = await axios.get(url);
        } catch {
          res = await axios.get(url, { headers: this.adminBearerGetHeaders() });
        }
        const d = res.data || {};
        if (d.assigned_count != null && !Number.isNaN(Number(d.assigned_count))) {
          assigned_total = Number(d.assigned_count);
        } else {
          assigned_total = await this.fetchMarkerAssignedFallback(year);
        }
      }

      if (!this._isMounted) return;
      const at = assigned_total || 0;
      this.setState({
        markerAssessedCount,
        assigned_total: at,
        marked_scripts: scripts,
        statsLoading: { assessed: false, assigned: false }
      });
      writeYearStatsCache("marker", year, { assigned_total: at });
    } catch {
      if (!this._isMounted) return;
      this.setState({
        statsLoading: { assessed: false, assigned: false }
      });
    }
  };

  fetchMarkerAssignedFallback(year) {
    return new Promise(resolve => {
      const data = { marker_codes: this.state.marker_codes, year };
      axios({
        headers: this.adminAuthHeaders(),
        method: "post",
        url: "https://admin.thinkdigitalacademy.org/api/get-assigned-scripts",
        data
      })
        .then(res => {
          const n =
            res.data && res.data.assigned_count != null
              ? Number(res.data.assigned_count)
              : 0;
          resolve(n);
        })
        .catch(() => resolve(0));
    });
  }

  /**
   * JSON POSTs to admin API — same minimal headers as AssignedTable / MarkedTable
   * (`content-type` only). Bearer added when present. Omit Accept / X-Requested-With
   * so CORS preflight matches what Laravel typically allows (avoids blocked requests
   * from marker.thinkdigitalacademy.org → admin).
   */
  adminAuthHeaders() {
    const token = localStorage.getItem("access_token");
    return {
      "content-type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  }

  /** Admin GET with Bearer only — avoids extra headers that break CORS (see ViewMarkedScriptToolbarSelect). */
  adminBearerGetHeaders() {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  getUserMarkerCodes() {
    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");
    axios
      .get(
        `https://markerportalbackend.thinkdigitalacademy.org/api/get-user-marker-codes/${user_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(res => {
        if (!this._isMounted) return;
        const codes = res.data.marker_codes || [];
        this.setState({ marker_codes: codes }, () => {
          if (!this._isMounted) return;
          if (codes.length > 0) {
            this.refreshMarkerDashboardStats(this.state.statsYear);
          } else {
            this.setState(s => ({
              statsLoading: {
                ...s.statsLoading,
                assessed: false,
                assigned: false
              }
            }));
          }
        });
      })
      .catch(error => {
        if (!this._isMounted) return;
        this.setState(s => ({
          statsLoading: {
            ...s.statsLoading,
            assessed: false,
            assigned: false
          }
        }));
        toast("An error occurred getting marer codes." + error, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          type: toast.TYPE.ERROR
        });
      });
  }

  render() {
    return (
      <>
       <ToastContainer position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover/>
                <ToastContainer/>
        <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
          <Container fluid>
            <div className="header-body">
              {this.state.role === "app-admin" ||
              this.state.role === "marker" ? (
                <>
                  <Row className="mb-3 align-items-end">
                    <Col xs="12" md="4" lg="3">
                      <FormGroup className="mb-0">
                        <Label
                          for="dashboard-stats-year"
                          className="text-white mb-1"
                        >
                          Reporting year
                        </Label>
                        <Input
                          id="dashboard-stats-year"
                          type="select"
                          bsSize="sm"
                          className="text-dark"
                          value={this.state.statsYear}
                          onChange={this.handleStatsYearChange}
                        >
                          {(() => {
                            const { min, max } = getCurrentStatsYearBounds();
                            const opts = [];
                            for (let y = max; y >= min; y -= 1) {
                              opts.push(
                                <option key={y} value={y}>
                                  {y}
                                </option>
                              );
                            }
                            return opts;
                          })()}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                 <Col lg="4" xl="4">
                   <Card
                     className={`card-stats mb-4 mb-xl-0${
                       this.state.statsLoading.assessed
                         ? " card-stats--stat-loading"
                         : ""
                     }`}
                   >
                     <CardBody>
                       <Row>
                         <div className="col">
                           <CardTitle
                             tag="h5"
                             className="text-uppercase text-muted mb-0"
                           >
                             Assessed Scripts
                           </CardTitle>
                           {this.state.statsLoading.assessed ? (
                             <DashboardStatSkeleton label="assessed scripts count" />
                           ) : this.state.role === "app-admin" ? (
                             <span className="h2 font-weight-bold mb-0">
                               {this.state.admin_marked}
                             </span>
                           ) : (
                             <span className="h2 font-weight-bold mb-0">
                               {this.state.markerAssessedCount}
                             </span>
                           )}
                         </div>
                         <Col className="col-auto">
                           <div className="icon icon-shape bg-success text-white rounded-circle shadow">
                             <i className="fas fa-chart-bar" />
                           </div>
                         </Col>
                       </Row>
                     </CardBody>
                     <CardFooter>
                     <Button onClick={()=>{window.location.href='/admin/marked'}} size="sm" color="info" outline type="button">
                       View
                     </Button>
                     </CardFooter>
                   </Card>
                 </Col>
                 <Col lg="4" xl="4">
                   <Card
                     className={`card-stats mb-4 mb-xl-0${
                       this.state.statsLoading.assigned
                         ? " card-stats--stat-loading"
                         : ""
                     }`}
                   >
                     <CardBody>
                       <Row>
                         <div className="col">
                           <CardTitle
                             tag="h5"
                             className="text-uppercase text-muted mb-0"
                           >
                             Assigned Scripts
                           </CardTitle>
                           {this.state.statsLoading.assigned ? (
                             <DashboardStatSkeleton label="assigned scripts count" />
                           ) : (
                             <span className="h2 font-weight-bold mb-0">
                               {this.state.assigned_total}
                             </span>
                           )}
                         </div>
                         <Col className="col-auto">
                           <div className="icon icon-shape bg-warning text-white rounded-circle shadow">
                             <i className="ni ni-chart-pie-35" />
                           </div>
                         </Col>
                       </Row>
 
                     </CardBody>
                     <CardFooter>
                     <Button onClick={()=>{window.location.href='/admin/assigned'}} size="sm" color="info" outline type="button">
                       View
                     </Button>
                     </CardFooter>
                   </Card>
                 </Col>
                 {
                   this.state.role === 'app-admin'&&<Col lg="4" xl="4">
                   <Card
                     className={`card-stats mb-4 mb-xl-0${
                       this.state.statsLoading.available
                         ? " card-stats--stat-loading"
                         : ""
                     }`}
                   >
                     <CardBody>
                       <Row>
                         <div className="col">
                           <CardTitle
                             tag="h5"
                             className="text-uppercase text-muted mb-0"
                           >
                             Available Scripts
                           </CardTitle>
                           {this.state.statsLoading.available ? (
                             <DashboardStatSkeleton label="available scripts count" />
                           ) : (
                             <span className="h2 font-weight-bold mb-0">
                               {this.state.admin_available}
                             </span>
                           )}
                         </div>
                         <Col className="col-auto">
                           <div className="icon icon-shape bg-warning text-white rounded-circle shadow">
                             <i className="ni ni-bullet-list-67" />
                           </div>
                         </Col>
                       </Row>
 
                     </CardBody>
                     <CardFooter>
                     <Button onClick={()=>{window.location.href='/admin/available'}} size="sm" color="info" outline type="button">
                       View
                     </Button>
                     </CardFooter>
                   </Card>
                 </Col>
                 }
                 
               </Row>
               {this.state.role === "app-admin" ? (
                 <>
                   <AdminTierAMetrics
                     funnel={this.state.funnel}
                     funnelLoading={this.state.funnelLoading}
                     admin_available={this.state.admin_available}
                     assigned_total={this.state.assigned_total}
                     admin_marked={this.state.admin_marked}
                     statsLoading={this.state.statsLoading}
                   />
                   <AdminMarkerWorkloadSection
                     funnel={this.state.funnel}
                     funnelLoading={this.state.funnelLoading}
                     statsYear={this.state.statsYear}
                     statsLoading={this.state.statsLoading}
                   />
                   <AdminMarkerWorkloadCharts
                     funnel={this.state.funnel}
                     funnelLoading={this.state.funnelLoading}
                     statsYear={this.state.statsYear}
                     statsLoading={this.state.statsLoading}
                   />
                 </>
               ) : null}
                </>
              ) : null}
              
            </div>
          </Container>
        </div>
      </>
    );
  }
}
export default connect(
  null,mapDispatchToProps
)(Header);
